import React, { useCallback, useEffect, useState } from 'react';
import { ACTIVITY_LABELS, getMemberActivity } from '../firebase/activityLog';
import './MemberActivityLog.css';

const INITIAL_VISIBLE = 5;

// Relative time, e.g. "2 hours ago"
const timeAgo = (timestamp) => {
  if (!timestamp) return '';

  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';

  const units = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 }
  ];

  for (const unit of units) {
    const value = Math.floor(seconds / unit.seconds);
    if (value >= 1) return `${value} ${unit.label}${value === 1 ? '' : 's'} ago`;
  }

  return 'just now';
};

const formatTimestamp = (entry) => {
  const date = entry.performedAt ? new Date(entry.performedAt) : new Date(entry.timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

// Recent changes made to a member's details, newest first
function MemberActivityLog({ tenantId, memberId, refreshKey = 0 }) {
  const [activity, setActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const loadActivity = useCallback(async () => {
    if (!tenantId || !memberId) return;

    setIsLoading(true);
    setError(null);

    const result = await getMemberActivity(tenantId, memberId);

    if (result.success) {
      setActivity(result.activity);
    } else {
      setError(result.message || 'Failed to load recent changes');
    }

    setIsLoading(false);
  }, [tenantId, memberId]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity, refreshKey]);

  const visibleActivity = showAll ? activity : activity.slice(0, INITIAL_VISIBLE);

  return (
    <div className="member-activity-section">
      <div className="member-activity-header">
        <h3>🕒 Recent Changes</h3>
        <button type="button" className="member-activity-refresh" onClick={loadActivity}>
          ↻ Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="member-activity-empty">Loading recent changes...</div>
      ) : error ? (
        <div className="member-activity-error">{error}</div>
      ) : activity.length === 0 ? (
        <div className="member-activity-empty">
          No changes recorded yet. Edits, pauses and resumes will appear here.
        </div>
      ) : (
        <>
          <ul className="member-activity-list">
            {visibleActivity.map((entry) => {
              const meta = ACTIVITY_LABELS[entry.type] || { icon: '📝', label: 'Change' };

              return (
                <li key={entry.id} className={`member-activity-item ${entry.type}`}>
                  <span className="member-activity-icon" aria-hidden="true">
                    {meta.icon}
                  </span>

                  <div className="member-activity-body">
                    <div className="member-activity-title">
                      <span className="member-activity-type">{meta.label}</span>
                      <span className="member-activity-time" title={formatTimestamp(entry)}>
                        {timeAgo(entry.timestamp)}
                      </span>
                    </div>

                    {entry.summary && <p className="member-activity-summary">{entry.summary}</p>}

                    {Array.isArray(entry.changes) && entry.changes.length > 0 && (
                      <ul className="member-activity-changes">
                        {entry.changes.map((change, index) => (
                          <li key={`${entry.id}_${change.field || index}`}>
                            <span className="change-field">{change.label || change.field}</span>
                            <span className="change-from">{change.from}</span>
                            <span className="change-arrow">→</span>
                            <span className="change-to">{change.to}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="member-activity-meta">
                      By {entry.performedBy || 'system'} · {formatTimestamp(entry)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {activity.length > INITIAL_VISIBLE && (
            <button
              type="button"
              className="member-activity-toggle"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? 'Show less' : `Show all ${activity.length} changes`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default MemberActivityLog;
